import { useNavigate } from "@entry/navigation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { CheckBox } from "@components/CheckBox";
import { ErrorComponent } from "@components/ErrorComponent";
import { Row } from "@components/Flex";
import { DateInput, TextInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { Flow, Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useGoalFlow, useTheme, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { validationSchema } from "./content";
import { styles } from "./styles";
import { AddBeneficiaryFormValues } from "./types";

const MAXIMUM_DATE = new Date();
const MINIMUM_DUE_DATE = new Date();

export const AddChildRoute = () => {
  const { theme } = useTheme();

  const [isFutureChild, setIsFutureChild] = useState(false);
  const [birthDay, setBirthDay] = useState<Date>();
  const [dueDate, setDueDate] = useState<Date>();

  const { user, loading, error } = useUser();

  const navigate = useNavigate();

  const {
    goalProps: { flow },
    setGoalProps,
  } = useGoalFlow();

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid: isValidForm },
  } = useForm<AddBeneficiaryFormValues>({
    defaultValues: {
      firstName: "",
      lastName: user?.lastName ?? "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const toggleFutureChild = () => {
    if (!isFutureChild) {
      const currentName = watch("firstName").trim();
      if (!currentName) {
        setValue("firstName", i18n.t(Translations.AddChildRouteFutureBabyText), constants.form);
      }
    }

    setIsFutureChild(!isFutureChild);
  };

  const isValid = isFutureChild ? isValidForm : isValidForm && birthDay;

  const firstNameController = register("firstName");
  const lastNameController = register("lastName");

  const handleSubmit = ({ firstName, lastName }: AddBeneficiaryFormValues) => {
    setGoalProps({
      firstName,
      lastName,
      dateOfBirth: isFutureChild ? dueDate : birthDay,
      isFutureBeneficiary: isFutureChild,
    });

    navigate(Routes.GoalType);
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.AddChildRouteTitle),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.AddChildRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
      },
    },
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...firstNameController}
        value={watch("firstName")}
        onChangeText={(value) => setValue("firstName", value, constants.form)}
        label={i18n.t(Translations.AddChildRouteFirstNameLabel)}
        placeholder={i18n.t(Translations.AddChildRouteFirstNamePlaceholder)}
        error={errors.firstName?.message}
      />
      <Spacing size={SpacingSizes.M} />

      <TextInput
        {...lastNameController}
        value={watch("lastName")}
        onChangeText={(value) => setValue("lastName", value, constants.form)}
        label={i18n.t(Translations.AddChildRouteLastNameLabel)}
        placeholder={i18n.t(Translations.AddChildRouteLastNamePlaceholder)}
        error={errors.lastName?.message}
      />
      <Spacing size={SpacingSizes.M} />

      {isFutureChild ? (
        <DateInput
          label={i18n.t(Translations.AddChildRouteDueDateLabel)}
          value={dueDate}
          onChange={setDueDate}
          minimumDate={MINIMUM_DUE_DATE}
        />
      ) : (
        <DateInput
          label={i18n.t(Translations.AddChildRouteDateOfBirthLabel)}
          value={birthDay}
          onChange={setBirthDay}
          maximumDate={MAXIMUM_DATE}
        />
      )}
      <Spacing size={SpacingSizes.M} />

      <Row style={styles.container} onClick={toggleFutureChild}>
        <CheckBox
          value={isFutureChild}
          style={styles.checkbox}
          unfilledColor={theme.checkbox.variant}
        />
        <Caption>{i18n.t(Translations.AddChildRouteFutureChild)}</Caption>
      </Row>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
